/**
 * Theme initialization and management
 * 
 * This module auto-initializes theme on import to prevent FOUC (Flash of Unstyled Content).
 * It also exports utility functions for theme toggling.
 * 
 * Theme detection priority:
 * 1. localStorage 'theme' value
 * 2. System preference via prefers-color-scheme
 * 3. Default (light theme, empty string)
 */

// =============================================================================
// Types
// =============================================================================

export type Theme = 'dark' | 'light' | '';

// =============================================================================
// Theme Detection & Application
// =============================================================================

/**
 * Detect the preferred theme based on localStorage or system preference
 */
export function detectTheme(): Theme {
    // Check localStorage first
    const saved = typeof localStorage !== 'undefined'
        ? localStorage.getItem('theme') as Theme | null
        : null;

    if (saved === 'dark' || saved === 'light') {
        return saved;
    }

    // Fall back to system preference
    if (typeof matchMedia !== 'undefined' && matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
    }

    return '';
}

/**
 * Apply theme to the document
 */
export function applyTheme(theme: Theme): void {
    if (typeof document === 'undefined') return;

    const html = document.documentElement;
    if (theme) {
        html.dataset.theme = theme;
    } else {
        delete html.dataset.theme;
    }
}

/**
 * Save theme preference to localStorage
 */
export function saveTheme(theme: Theme): void {
    if (typeof localStorage === 'undefined') return;

    if (theme) {
        localStorage.setItem('theme', theme);
    } else {
        localStorage.removeItem('theme');
    }
}

/**
 * Get the current theme from the document
 */
export function getCurrentTheme(): Theme {
    if (typeof document === 'undefined') return '';
    return (document.documentElement.dataset.theme as Theme) || '';
}

// =============================================================================
// Theme Toggle
// =============================================================================

/**
 * Toggle between light and dark themes
 * @returns The new theme after toggling
 */
export function toggleTheme(): Theme {
    const current = getCurrentTheme();
    const newTheme: Theme = current === 'dark' ? '' : 'dark';

    applyTheme(newTheme);
    saveTheme(newTheme);

    return newTheme;
}

/**
 * Set a specific theme
 */
export function setTheme(theme: Theme): void {
    applyTheme(theme);
    saveTheme(theme);
}

// =============================================================================
// Ready Helper
// =============================================================================

/**
 * Remove loading class when components are ready
 * Call this after your app is initialized
 */
export function ready(): void {
    if (typeof document === 'undefined') return;
    document.documentElement.classList.remove('loading');
}

// =============================================================================
// Auto-initialization
// =============================================================================

// Run immediately on module load (not in SSR context)
if (typeof document !== 'undefined') {
    // Detect and apply theme immediately
    const theme = detectTheme();
    applyTheme(theme);
}
